import type {
    Language,
    Operation,
    JsonOperation,
    XmlOperation,
    HtmlOperation,
    TextOperation,
    OperationConfig,
    FormattingResult,
} from "./types";

export class FormatterService {
    /**
     * Format text based on language and operation
     */
    static format(
        input: string,
        language: Language,
        operation: Operation,
        config: OperationConfig
    ): FormattingResult {
        try {
            switch (language) {
                case "json":
                    return this.formatJson(input, operation as JsonOperation, config);
                case "xml":
                    return this.formatXml(input, operation as XmlOperation, config);
                case "html":
                    return this.formatHtml(input, operation as HtmlOperation, config);
                case "text":
                    return this.formatText(input, operation as TextOperation);
                default:
                    return {
                        success: false,
                        output: "",
                        error: `Unknown language: ${language}`,
                    };
            }
        } catch (error) {
            return {
                success: false,
                output: "",
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }

    /**
     * Format JSON content
     */
    private static formatJson(
        input: string,
        operation: JsonOperation,
        config: OperationConfig
    ): FormattingResult {
        try {
            const trimmedInput = input.trim();
            if (!trimmedInput) {
                return { success: true, output: "" };
            }

            switch (operation) {
                case "format": {
                    const parsed = JSON.parse(trimmedInput);
                    const indentSize = config.indentSize || 2;
                    let formatted = JSON.stringify(parsed, null, indentSize);

                    if (config.sortKeys) {
                        formatted = JSON.stringify(this.sortObjectKeys(parsed), null, indentSize);
                    }

                    return { success: true, output: formatted };
                }

                case "minify": {
                    const parsed = JSON.parse(trimmedInput);
                    return { success: true, output: JSON.stringify(parsed) };
                }

                case "validate": {
                    JSON.parse(trimmedInput);
                    return {
                        success: true,
                        output: "✓ Valid JSON",
                    };
                }

                default:
                    return {
                        success: false,
                        output: "",
                        error: `Unknown JSON operation: ${operation}`,
                    };
            }
        } catch (error) {
            return {
                success: false,
                output: "",
                error: `JSON Error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Format XML content
     */
    private static formatXml(
        input: string,
        operation: XmlOperation,
        config: OperationConfig
    ): FormattingResult {
        try {
            const trimmedInput = input.trim();
            if (!trimmedInput) {
                return { success: true, output: "" };
            }

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(trimmedInput, "text/xml");

            const parserError = xmlDoc.querySelector("parsererror");
            if (parserError) {
                throw new Error(parserError.textContent || "XML parsing failed");
            }

            switch (operation) {
                case "format": {
                    const indentSize = config.indentSize || 2;
                    const formatted = this.formatXmlNode(xmlDoc, 0, indentSize);
                    return { success: true, output: formatted };
                }

                case "validate": {
                    return {
                        success: true,
                        output: "✓ Valid XML",
                    };
                }

                default:
                    return {
                        success: false,
                        output: "",
                        error: `Unknown XML operation: ${operation}`,
                    };
            }
        } catch (error) {
            return {
                success: false,
                output: "",
                error: `XML Error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Format HTML content
     */
    private static formatHtml(input: string, operation: HtmlOperation, config?: any): FormattingResult {
        try {
            const trimmedInput = input.trim();
            if (!trimmedInput) {
                return { success: false, output: "", error: "Input is empty" };
            }

            switch (operation) {
                case "format": {
                    // Parse HTML using DOMParser
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(trimmedInput, "text/html");

                    // Check for parsing errors
                    const parserError = doc.querySelector("parsererror");
                    if (parserError) {
                        return {
                            success: false,
                            output: "",
                            error: `HTML parsing error: ${parserError.textContent}`,
                        };
                    }

                    // Format HTML with indentation
                    const formatted = this.formatHtmlNode(doc.documentElement, 0);
                    return { success: true, output: formatted };
                }

                case "validate": {
                    // Parse HTML to check for errors
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(trimmedInput, "text/html");

                    const parserError = doc.querySelector("parsererror");
                    if (parserError) {
                        return {
                            success: false,
                            output: "",
                            error: `HTML validation failed: ${parserError.textContent}`,
                        };
                    }

                    return { success: true, output: "HTML is valid" };
                }

                default:
                    return {
                        success: false,
                        output: "",
                        error: `Unknown HTML operation: ${operation}`,
                    };
            }
        } catch (error) {
            return {
                success: false,
                output: "",
                error: `HTML Error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Recursively format HTML node with indentation
     */
    private static formatHtmlNode(node: Node, depth: number): string {
        const indent = "  ".repeat(depth);
        const nextIndent = "  ".repeat(depth + 1);

        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            return text ? text : "";
        }

        if (node.nodeType === Node.COMMENT_NODE) {
            return `${indent}<!--${node.textContent}-->`;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const tagName = element.tagName.toLowerCase();
            const attributes = Array.from(element.attributes)
                .map((attr) => `${attr.name}="${attr.value}"`)
                .join(" ");

            const openTag = attributes ? `<${tagName} ${attributes}>` : `<${tagName}>`;

            // Self-closing tags
            const selfClosingTags = ["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"];
            if (selfClosingTags.includes(tagName) && element.childNodes.length === 0) {
                return `${indent}${openTag}`;
            }

            // Format children
            const children: string[] = [];
            let hasBlockChildren = false;

            for (let i = 0; i < element.childNodes.length; i++) {
                const child = element.childNodes[i];
                if (child.nodeType === Node.ELEMENT_NODE) {
                    hasBlockChildren = true;
                }
                const formatted = this.formatHtmlNode(child, depth + 1);
                if (formatted) {
                    children.push(formatted);
                }
            }

            const closeTag = `</${tagName}>`;

            if (children.length === 0) {
                return `${indent}${openTag}${closeTag}`;
            }

            // Inline formatting for elements with only text content
            if (!hasBlockChildren && children.length === 1) {
                return `${indent}${openTag}${children[0]}${closeTag}`;
            }

            // Block formatting for elements with child elements
            const childrenText = children.map((child) => (child.startsWith(nextIndent) ? child : `${nextIndent}${child}`)).join("\n");
            return `${indent}${openTag}\n${childrenText}\n${indent}${closeTag}`;
        }

        return "";
    }

    /**
     * Format text content
     */
    private static formatText(input: string, operation: TextOperation): FormattingResult {
        try {
            switch (operation) {
                case "base64encode": {
                    const encoded = btoa(input);
                    return { success: true, output: encoded };
                }

                case "base64decode": {
                    const decoded = atob(input);
                    return { success: true, output: decoded };
                }

                case "urlencode": {
                    const encoded = encodeURIComponent(input);
                    return { success: true, output: encoded };
                }

                case "urldecode": {
                    const decoded = decodeURIComponent(input);
                    return { success: true, output: decoded };
                }

                case "htmlencode": {
                    const encoded = this.htmlEncode(input);
                    return { success: true, output: encoded };
                }

                case "htmldecode": {
                    const decoded = this.htmlDecode(input);
                    return { success: true, output: decoded };
                }

                case "removelinebreaks": {
                    const cleaned = input.replace(/\r?\n|\r/g, " ").replace(/\s+/g, " ");
                    return { success: true, output: cleaned };
                }

                case "uppercase": {
                    const result = this.toUppercase(input);
                    return { success: true, output: result };
                }

                case "lowercase": {
                    const result = this.toLowercase(input);
                    return { success: true, output: result };
                }

                case "snakecase": {
                    const result = this.toSnakeCase(input);
                    return { success: true, output: result };
                }

                case "kebabcase": {
                    const result = this.toKebabCase(input);
                    return { success: true, output: result };
                }

                case "pascalcase": {
                    const result = this.toPascalCase(input);
                    return { success: true, output: result };
                }

                case "camelcase": {
                    const result = this.toCamelCase(input);
                    return { success: true, output: result };
                }

                default:
                    return {
                        success: false,
                        output: "",
                        error: `Unknown text operation: ${operation}`,
                    };
            }
        } catch (error) {
            return {
                success: false,
                output: "",
                error: `Text Error: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
    }

    /**
     * Encode special HTML characters into their entity equivalents
     */
    private static htmlEncode(input: string): string {
        return input
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    /**
     * Decode HTML entities back into their literal characters
     * Uses a detached textarea so the browser resolves named, decimal, and hex entities
     */
    private static htmlDecode(input: string): string {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = input;
        return textarea.value;
    }

    /**
     * Normalize whitespace within lines while preserving line breaks
     * Converts multiple spaces to single space
     */
    private static normalizeWhitespace(text: string): string {
        return text
            .split(/\r?\n/)
            .map((line) => line.replace(/\s+/g, " "))
            .join("\n");
    }

    /**
     * Split text into words, treating spaces, hyphens, underscores, and case transitions as boundaries
     * Also tracks case transitions to preserve them in PascalCase and camelCase
     */
    private static extractWords(text: string): string[] {
        // Split on spaces, hyphens, underscores, and case transitions
        // This regex matches word boundaries created by:
        // - Spaces
        // - Hyphens and underscores (treated as separators, not kept)
        // - Transitions from lowercase to uppercase
        return text
            .split(/[\s\-_]+|(?=[A-Z])|(?<=[a-z])(?=[A-Z])/)
            .filter((word) => word.length > 0);
    }

    /**
     * Capitalize first character of a word while preserving the rest as-is
     * Used for PascalCase to retain uppercase characters in original text
     */
    private static capitalizeWord(word: string): string {
        if (word.length === 0) return "";
        return word.charAt(0).toUpperCase() + word.slice(1);
    }

    /**
     * Convert text to uppercase
     */
    private static toUppercase(input: string): string {
        return this.normalizeWhitespace(input).toUpperCase();
    }

    /**
     * Convert text to lowercase
     */
    private static toLowercase(input: string): string {
        return this.normalizeWhitespace(input).toLowerCase();
    }

    /**
     * Convert text to snake_case
     * Hyphens and underscores treated as word boundaries, underscores used as separators
     * Preserves line breaks
     */
    private static toSnakeCase(input: string): string {
        const normalized = this.normalizeWhitespace(input);
        return normalized
            .split(/\r?\n/)
            .map((line) => {
                const words = this.extractWords(line.trim());
                return words.map((word) => word.toLowerCase()).join("_");
            })
            .join("\n");
    }

    /**
     * Convert text to hyphen-case
     * Underscores and hyphens treated as word boundaries, hyphens used as separators
     * Preserves line breaks
     */
    private static toKebabCase(input: string): string {
        const normalized = this.normalizeWhitespace(input);
        return normalized
            .split(/\r?\n/)
            .map((line) => {
                const words = this.extractWords(line.trim());
                return words.map((word) => word.toLowerCase()).join("-");
            })
            .join("\n");
    }

    /**
     * Convert text to PascalCase
     * Hyphens and underscores treated as word boundaries
     * Retains uppercase characters from lowercase-to-uppercase transitions in original text
     * Preserves line breaks
     */
    private static toPascalCase(input: string): string {
        const normalized = this.normalizeWhitespace(input);
        return normalized
            .split(/\r?\n/)
            .map((line) => {
                const words = this.extractWords(line.trim());
                return words.map((word) => this.capitalizeWord(word)).join("");
            })
            .join("\n");
    }

    /**
     * Convert text to camelCase
     * Hyphens and underscores treated as word boundaries
     * Retains uppercase characters from lowercase-to-uppercase transitions in original text
     * First word is lowercase, subsequent words have uppercase first character
     * Preserves line breaks
     */
    private static toCamelCase(input: string): string {
        const normalized = this.normalizeWhitespace(input);
        return normalized
            .split(/\r?\n/)
            .map((line) => {
                const words = this.extractWords(line.trim());
                if (words.length === 0) return "";
                return (
                    words[0].toLowerCase() +
                    words.slice(1).map((word) => this.capitalizeWord(word)).join("")
                );
            })
            .join("\n");
    }

    /**
     * Recursively sort object keys
     */
    private static sortObjectKeys(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map((item) => this.sortObjectKeys(item));
        } else if (obj !== null && typeof obj === "object") {
            const sorted: any = {};
            Object.keys(obj)
                .sort()
                .forEach((key) => {
                    sorted[key] = this.sortObjectKeys(obj[key]);
                });
            return sorted;
        }
        return obj;
    }

    /**
     * Format XML node recursively with proper indentation
     */
    private static formatXmlNode(node: Node, depth: number, indentSize: number): string {
        const indent = " ".repeat(depth * indentSize);
        const nextIndent = " ".repeat((depth + 1) * indentSize);

        if (node.nodeType === Node.DOCUMENT_NODE) {
            const docNode = node as Document;
            let result = '<?xml version="1.0"?>\n';

            for (let i = 0; i < docNode.childNodes.length; i++) {
                result += this.formatXmlNode(docNode.childNodes[i], depth, indentSize);
            }

            return result;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            let result = `${indent}<${element.tagName}`;

            for (let i = 0; i < element.attributes.length; i++) {
                const attr = element.attributes[i];
                result += ` ${attr.name}="${attr.value}"`;
            }

            if (element.childNodes.length === 0) {
                return `${result} />\n`;
            }

            result += ">";

            const hasElementChildren = Array.from(element.childNodes).some(
                (child) => child.nodeType === Node.ELEMENT_NODE
            );

            if (hasElementChildren) {
                result += "\n";
                for (let i = 0; i < element.childNodes.length; i++) {
                    result += this.formatXmlNode(element.childNodes[i], depth + 1, indentSize);
                }
                result += `${indent}</${element.tagName}>\n`;
            } else {
                let textContent = "";
                for (let i = 0; i < element.childNodes.length; i++) {
                    const child = element.childNodes[i];
                    if (child.nodeType === Node.TEXT_NODE) {
                        textContent += child.textContent || "";
                    }
                }
                textContent = textContent.trim();
                result += textContent;
                result += `</${element.tagName}>\n`;
            }

            return result;
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const text = (node.textContent || "").trim();
            if (text) {
                return `${indent}${text}\n`;
            }
        }

        return "";
    }

    /**
     * Get available operations for a language
     */
    static getOperationsForLanguage(language: Language): Operation[] {
        switch (language) {
            case "json":
                return ["format", "minify", "validate"];
            case "xml":
                return ["format", "validate"];
            case "html":
                return ["format", "validate"];
            case "text":
                return [
                    "base64encode",
                    "base64decode",
                    "urlencode",
                    "urldecode",
                    "htmlencode",
                    "htmldecode",
                    "removelinebreaks",
                    "uppercase",
                    "lowercase",
                    "snakecase",
                    "kebabcase",
                    "pascalcase",
                    "camelcase",
                ];
            default:
                return [];
        }
    }

    /**
     * Get friendly operation name
     */
    static getOperationLabel(operation: Operation): string {
        const labels: Record<string, string> = {
            format: "Format / Beautify",
            minify: "Minify",
            validate: "Validate",
            base64encode: "Base64 Encode",
            base64decode: "Base64 Decode",
            urlencode: "URL Encode",
            urldecode: "URL Decode",
            htmlencode: "HTML Encode",
            htmldecode: "HTML Decode",
            removelinebreaks: "Remove Line Breaks",
            uppercase: "UPPERCASE",
            lowercase: "lowercase",
            snakecase: "snake_case",
            kebabcase: "kebab-case",
            pascalcase: "PascalCase",
            camelcase: "camelCase",
        };
        return labels[operation] || operation;
    }

    /**
     * Check if operation requires config options
     */
    static requiresConfig(language: Language, operation: Operation): boolean {
        if (language === "json" && operation === "format") {
            return true;
        }
        if (language === "xml" && operation === "format") {
            return true;
        }
        if (language === "html" && operation === "format") {
            return true;
        }
        return false;
    }
}
